import { SUBSCRIPTION_STATUS, SubscriptionType } from '~/types/subscription';

// QA-only override for the current subscription. Lets testers reproduce any
// subscription state (cancelled / redemption / cancel_pending / …) on staging
// without a real matching account, by passing `?mock-sub=<status>&mock-trial=<bool>`.
// Gated to non-production backends and persisted for the session so it survives
// in-app navigation and profile refreshes. `?mock-sub=off` clears it.

const STATUS_PARAM = 'mock-sub';
const TRIAL_PARAM = 'mock-trial';
const OFF_VALUE = 'off';
const STORAGE_KEY = 'chatwithai_qa_sub_mock';

type QaSubscriptionMock = {
  status: SUBSCRIPTION_STATUS;
  is_trial?: boolean;
};

// Only enable against local/staging backends — never production.
function isQaMockEnabled(): boolean {
  const base = process.env.REST_API_BASE_URL || '';
  return /-stage|localhost|127\.0\.0\.1/.test(base);
}

function isValidStatus(value: string): value is SUBSCRIPTION_STATUS {
  return (Object.values(SUBSCRIPTION_STATUS) as string[]).includes(value);
}

function readStored(): QaSubscriptionMock | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      isValidStatus((parsed as QaSubscriptionMock).status)
    ) {
      return parsed as QaSubscriptionMock;
    }
  } catch {
    // sessionStorage unavailable or corrupt — behave as if no mock is set.
  }
  return null;
}

function writeStored(mock: QaSubscriptionMock): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(mock));
  } catch {
    // Ignore storage failures — the mock simply won't persist.
  }
}

function clearStored(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function resolveMock(): QaSubscriptionMock | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return readStored();
  }

  const status = params.get(STATUS_PARAM);

  if (status === OFF_VALUE) {
    clearStored();
    return null;
  }

  if (status && isValidStatus(status)) {
    const mock: QaSubscriptionMock = { status };
    const trial = params.get(TRIAL_PARAM);
    if (trial === 'true') mock.is_trial = true;
    else if (trial === 'false') mock.is_trial = false;
    writeStored(mock);
    return mock;
  }

  // No valid param in the URL — fall back to a persisted mock, if any.
  return readStored();
}

export function applyQaSubscriptionMock(
  subscription: SubscriptionType,
): SubscriptionType {
  if (!isQaMockEnabled()) return subscription;

  const mock = resolveMock();
  if (!mock) return subscription;

  return {
    ...subscription,
    status: mock.status,
    is_trial: mock.is_trial ?? subscription.is_trial,
  };
}
